// Shared pieces of the number pages: the growth read, source lines, ticks, and the capacity and delivery views both Funnel
// and Outreach show. A number nobody measured is a dash; a true zero is 0.
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../../../lib'
import { Fresh, Section, Sources, Table, type Column } from '../kit'
import type { Board, CapacityDay, Growth, Num, Source } from '../../growth/types'
import { current, n, plural, short, sum } from '../../growth/format'

/** GET /api/staff/growth: the app's live numbers and the last outreach snapshot, every 5 minutes. */
export function useGrowth(internal: boolean) {
  return useQuery({
    queryKey: ['staff', 'growth', internal],
    queryFn: () => api<Growth>('GET', `/api/staff/growth?day=founders${internal ? '&internal=1' : ''}`),
    refetchInterval: 300_000,
  })
}

/** "Show internal" kept in the URL. Off by default, with the same filter as the old pages. */
export function useInternal(): [boolean, ReactNode] {
  const [params, setParams] = useSearchParams()
  const on = params.get('internal') === '1'
  const box = (
    <label className="st-row st-note">
      <input type="checkbox" data-testid="growth-internal" checked={on} onChange={(e) => {
        const next = new URLSearchParams(params)
        if (e.target.checked) next.set('internal', '1'); else next.delete('internal')
        setParams(next, { replace: true })
      }} />
      Show internal
    </label>
  )
  return [on, box]
}

export const sourceOf = (b: Board | null | undefined, id: string) => b?.sources.find((s) => s.id === id)

/** Replies are read from one place, the Emails source. Until it's current a Replied count is a dash, never a 0 that looks true. */
export const repliesRead = (b: Board | null | undefined) => current(sourceOf(b, 'instantly'))

/** One source's name and age. Past its limit, failed or never read, it's red. */
export function SourceFresh({ s, label, testid }: { s: Source | undefined; label?: string; testid?: string }) {
  const name = label ?? s?.label ?? 'Source'
  return <Fresh at={s?.as_of} label={s?.run_failed ? `${name}, run failed,` : name} limit={current(s) ? undefined : 0} testid={testid} />
}

export function SourceLine({ b, ids }: { b: Board | null | undefined; ids: string[] }) {
  return <Sources>{ids.map((id) => <SourceFresh key={id} s={sourceOf(b, id)} label={sourceOf(b, id)?.label ?? id} testid={`growth-source-${id}`} />)}</Sources>
}

/** A record link: the company when the old prospect link can find it, plain text otherwise. */
export function ProspectLink({ domain, children }: { domain?: string | null; children: ReactNode }) {
  return domain ? <Link className="st-link" to={`/staff/growth/prospect/${encodeURIComponent(domain)}`}>{children}</Link> : <>{children}</>
}

/** One tick per email: lit up to `on`, faint after. Lit ticks take the dusk, left to right across the strip. */
export function Ticks({ total, on, name }: { total: number; on: Num; name: string }) {
  const lit = Math.min(on ?? 0, total)
  const STEP = 4, W = Math.max(total * STEP, 1)
  return (
    <svg className="st-ticks" viewBox={`0 0 ${W} 10`} preserveAspectRatio="none" role="img" aria-label={`${name}: ${n(on)} of ${total}`}>
      <title>{`${name}: ${n(on)} of ${total}`}</title>
      {Array.from({ length: total }, (_, i) => (
        <rect key={i} x={i * STEP + 1} y={0} width={STEP * 0.4} height={10} className={i < lit ? 'st-tick-on' : 'st-tick-off'} style={i < lit ? { fill: dusk(i / Math.max(total - 1, 1)) } : undefined} />
      ))}
    </svg>
  )
}

function dusk(t: number): string {
  const a = [0x6a, 0x97, 0xe6], b = [0x76, 0x82, 0xd7], c = [0x97, 0x75, 0xc6]
  const [p, q, u] = t < 0.5 ? [a, b, t * 2] : [b, c, (t - 0.5) * 2]
  return `rgb(${p.map((v, i) => Math.round(v + (q[i] - v) * u)).join(' ')})`
}

export function Bar({ value, max, title }: { value: number; max: number; title?: string }) {
  return <div className="st-bar-track" role="presentation"><div className="st-bar" style={{ width: `${Math.min(100, (value / Math.max(max, 1)) * 100)}%` }} title={title} /></div>
}

export function Lines({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return <div className="st-lines">{rows.map(([k, v], i) => <div key={i} className="st-line"><span>{k}</span><span className="st-num">{v}</span></div>)}</div>
}

/** Before the email, what limits sending, and sends against capacity. */
export function Sending({ b, ready, id }: { b: Board; ready: Num | undefined; id?: string }) {
  const p = b.pipeline
  if (!p) return null
  const stages = p.stages.filter((s) => ['sourced', 'researched', 'passed', 'email'].includes(s.stage))
  const max = Math.max(1, ...stages.map((r) => r.n))
  const top = p.reject_reasons[0]
  return (
    <Section id={id} title={p.limit ?? 'What limits sending'} testid="growth-sending">
      <div className="st-two">
        {b.capacity.length > 1 ? <CapacityChart rows={b.capacity} /> : <p className="st-note">The projection was not read in this run</p>}
        <div className="st-stack">
          <Lines rows={[['Capacity today', n(p.capacity_today)], ['New leads the pipeline adds a day', n(p.new_leads_a_day)], ['Ready to load now', n(ready)], ['Waiting for research', n(p.waiting_to_qualify)]]} />
          {stages.length ? (
            <div data-testid="growth-before">
              {stages.map((r) => <div key={r.stage} className="st-bar-row"><span>{r.label}</span><Bar value={r.n} max={max} title={`${r.label}: ${n(r.n)}`} /><span className="st-num">{n(r.n)}</span></div>)}
              {top ? <p className="st-note">Top reason companies drop out: {top.reason.replace(/^reject:/, '').replace(/_/g, ' ').replace(/\bai\b/g, 'AI')}</p> : null}
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  )
}

/** Bars stacked from one baseline and one capacity step line, all on one axis. Sent days are solid. */
function CapacityChart({ rows }: { rows: CapacityDay[] }) {
  const W = 760, H = 220, left = 36, base = H - 22
  const total = (r: CapacityDay) => (r.actual ?? 0) + (r.first_emails ?? 0) + (r.followups ?? 0)
  const top = niceMax(Math.max(1, ...rows.map((r) => Math.max(r.capacity ?? 0, total(r)))))
  const y = (v: number) => base - (v / top) * (base - 8)
  const band = (W - left) / rows.length
  const bar = Math.min(28, band - 8)
  const line = rows.flatMap((r, i) => (r.capacity === null ? [] : [`${left + i * band},${y(r.capacity)}`, `${left + (i + 1) * band},${y(r.capacity)}`])).join(' ')
  const labelled = new Set([0, Math.floor(rows.length / 2), rows.length - 1])
  const peak = rows.reduce((best, r, i) => (total(r) > total(rows[best]) ? i : best), 0)
  return (
    <div>
      <div className="st-legend">
        <span><i className="st-key st-key-solid" />Sent</span><span><i className="st-key st-key-mid" />First emails</span>
        <span><i className="st-key st-key-light" />Follow-ups</span><span><i className="st-key st-key-line" />Capacity</span>
      </div>
      <svg className="st-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Emails a day against sending capacity">
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={left} x2={W} y1={y(top * t)} y2={y(top * t)} className="st-chart-grid" />
            <text x={left - 8} y={y(top * t) + 4} textAnchor="end" className="st-chart-text">{n(top * t)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x = left + i * band + (band - bar) / 2
          const first = r.actual ?? r.first_emails ?? 0, follow = r.followups ?? 0
          const h1 = (first / top) * (base - 8), h2 = (follow / top) * (base - 8)
          const tip = `${short(r.date)}: ${r.actual !== null ? `${n(r.actual)} sent` : `${n(first)} first emails, ${n(follow)} follow-ups planned`}, capacity ${n(r.capacity)}`
          return (
            <g key={r.date}>
              <title>{tip}</title>
              <rect x={left + i * band} y={0} width={band} height={base} fill="transparent" />
              {first ? <rect x={x} y={base - h1} width={bar} height={h1} rx={2} className={r.actual !== null ? 'st-mark' : 'st-mark-mid'} /> : null}
              {follow ? <rect x={x} y={base - h1 - h2 - (first ? 2 : 0)} width={bar} height={h2} rx={2} className="st-mark-light" /> : null}
              {(i === 0 || i === peak) && total(r) ? <text x={x + bar / 2} y={base - h1 - h2 - 6} textAnchor="middle" className="st-chart-text">{n(total(r))}</text> : null}
              {labelled.has(i) ? <text x={x + bar / 2} y={H - 4} textAnchor="middle" className="st-chart-text">{short(r.date)}</text> : null}
            </g>
          )
        })}
        {line ? <polyline points={line} className="st-chart-line" /> : null}
      </svg>
    </div>
  )
}

function niceMax(v: number): number {
  const step = 10 ** Math.floor(Math.log10(v))
  return Math.ceil(v / step) * step
}

type EmailRow = { sent: Num; replied: Num; bounced: Num }
/** Keep a number column only when some row holds a number, so a table isn't a wall of dashes. */
const has = <T,>(rows: T[], v: (r: T) => Num) => rows.some((r) => v(r) !== null && v(r) !== undefined)
function emailCols<T extends EmailRow>(rows: T[], replies: boolean): Column<T>[] {
  const out: Column<T>[] = []
  if (has(rows, (r) => r.sent)) out.push({ key: 'sent', head: 'Sent', num: true, cell: (r) => n(r.sent) })
  if (replies && has(rows, (r) => r.replied)) out.push({ key: 'replied', head: 'Replied', num: true, cell: (r) => n(r.replied) })
  if (has(rows, (r) => r.bounced)) out.push({ key: 'bounced', head: 'Bounced', num: true, cell: (r) => n(r.bounced) })
  return out
}
const warmupCol = <T extends { warmup: Num }>(rows: T[]): Column<T>[] => (has(rows, (r) => r.warmup) ? [{ key: 'warmup', head: 'Warmup', num: true, cell: (r) => n(r.warmup) }] : [])

/** Campaigns, domains and mailboxes. Warmup and bounces are all that's measured here, so the line says that and no more:
 * nothing reads inbox placement or reputation. */
export function Delivery({ g }: { g: Growth }) {
  const o = g.outreach
  if (!o || (o.domains.length === 0 && o.campaigns.length === 0)) return <p className="st-note" id="campaigns" data-testid="outreach-no-delivery">Campaigns, domains and mailboxes weren't read</p>
  const bounced = sum(o.mailboxes.map((m) => m.bounced))
  const bouncing = (d: { sent: Num; bounced: Num }) => (d.sent ?? 0) >= 5 && (d.bounced ?? 0) >= 2
  const low = o.domains.filter((d) => (d.warmup_min ?? 100) < 90 || bouncing(d)).length + o.mailboxes.filter((m) => m.paused).length
  const unread = o.domains.some((d) => d.warmup_min === null || d.bounced === null)
  const read = low ? `, ${n(low)} ${low === 1 ? 'needs' : 'need'} a look` : unread ? ', not all read' : ', warmup 90 or more and few bounces'
  const line = `${plural(o.domains.length, 'sending domain')} and ${plural(o.mailboxes.length, 'mailbox', 'mailboxes')}${read}. ${bounced ? plural(bounced, 'bounce') : 'No bounces'}. Inbox placement is not measured`
  const replies = repliesRead(o.board)
  const domains = <Table rows={o.domains} rowKey={(r) => r.domain} testid="growth-domains" columns={[
    { key: 'domain', head: 'Domain', primary: true, cell: (r) => r.domain }, { key: 'campaign', head: 'Campaign', cell: (r) => r.campaign ?? null },
    ...(has(o.domains, (r) => r.mailboxes) ? [{ key: 'mailboxes', head: 'Mailboxes', num: true, cell: (r: typeof o.domains[number]) => n(r.mailboxes) }] : []), ...emailCols(o.domains, replies), ...warmupCol(o.domains),
  ]} />
  const boxes = <Table rows={o.mailboxes} rowKey={(r) => r.email} testid="growth-mailboxes" columns={[
    { key: 'email', head: 'Mailbox', primary: true, cell: (r) => <>{r.email}{r.paused ? <span className="st-muted"> paused</span> : null}</> },
    ...(has(o.mailboxes, (r) => r.daily_limit) ? [{ key: 'limit', head: 'Limit', num: true, cell: (r: typeof o.mailboxes[number]) => n(r.daily_limit) }] : []), ...emailCols(o.mailboxes, replies), ...warmupCol(o.mailboxes),
  ]} />
  const camps = <Table rows={o.campaigns} rowKey={(r) => `${r.letter}-${r.name}`} testid="growth-campaigns" columns={[
    { key: 'name', head: 'Campaign', primary: true, cell: (r) => r.name ?? r.letter ?? null }, { key: 'status', head: 'Status', cell: (r) => r.status ?? null },
    ...(has(o.campaigns, (r) => r.mailboxes) ? [{ key: 'mailboxes', head: 'Mailboxes', num: true, cell: (r: typeof o.campaigns[number]) => n(r.mailboxes) }] : []), ...emailCols(o.campaigns, replies),
  ]} />
  return (
    <>
      <Section id="campaigns" title="Campaigns" count={o.campaigns.length}><p className="st-note">{line}</p>{o.campaigns.length ? camps : null}</Section>
      {o.domains.length ? <Section id="domains" title="Domains" count={o.domains.length}>{domains}</Section> : null}
      {o.mailboxes.length ? <details className="st-td-details" id="mailboxes" data-testid="outreach-mailboxes"><summary>Mailboxes ({o.mailboxes.length})</summary>{boxes}</details> : null}
    </>
  )
}
